import { forwardRef } from 'react';

import { twMerge } from 'tailwind-merge';

type Ref = HTMLInputElement;

const radioClasses = twMerge(
  'peer h-4 w-4 shrink-0 cursor-pointer border-neutral-300 bg-white',
  'checked:text-blue-400 disabled:cursor-not-allowed',
  'dark:border-neutral-700 dark:bg-neutral-700',
  'dark:checked:border-blue-400',
  'dark:ring-offset-neutral-900 dark:focus:ring-blue-300',
);

const orientations = {
  horizontal: 'flex-row',
  vertical: 'flex-col',
};

interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly orientation?: keyof typeof orientations;
}

function RadioGroup({ children, orientation = 'horizontal', className, ...rest }: RadioGroupProps) {
  return (
    <div {...rest} className={twMerge('flex flex-col gap-2', orientations[orientation], className)}>
      {children}
    </div>
  );
}

const RadioDefault = forwardRef<Ref, React.InputHTMLAttributes<HTMLInputElement>>(function RadioDefault(props, ref) {
  const { className, children, ...rest } = props;

  return (
    <div className={twMerge('flex items-center', className)}>
      <input type="radio" ref={ref} className={radioClasses} {...rest} />
      <label
        htmlFor={rest.id}
        data-testid="radio"
        className={twMerge(
          'ml-3 inline-flex cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:text-neutral-400 dark:text-neutral-300',
        )}
      >
        {children}
      </label>
    </div>
  );
});

const RadioCard = forwardRef<Ref, React.InputHTMLAttributes<HTMLInputElement>>(function RadioCard(props, ref) {
  const { className, children, ...rest } = props;

  return (
    <div className={twMerge('relative flex flex-1', className)}>
      <input ref={ref} type="radio" className={twMerge(radioClasses, 'absolute left-2 top-2')} {...rest} />
      <label
        htmlFor={rest.id}
        data-testid="radio-card"
        className={twMerge(
          'peer:border-blue-400 h-full w-full flex-1 cursor-pointer rounded-md border border-neutral-300 p-6 peer-checked:border peer-checked:border-blue-400 peer-checked:ring-1 peer-checked:ring-blue-400 dark:border-neutral-700',
        )}
      >
        {children}
      </label>
    </div>
  );
});

const Radio = RadioDefault as typeof RadioDefault & {
  Group: typeof RadioGroup;
  Card: typeof RadioCard;
};

Radio.Group = RadioGroup;
Radio.Card = RadioCard;

export { Radio };
